<template>
    <div class="modal-card edit-announcement-modal">
        <header class="modal-card-head">
            <p class="modal-card-title">Edit Announcement</p>
        </header>
        <section class="modal-card-body">
            <div class="field">
                <label class="label">Title</label>
                <div class="control">
                    <input class="input" type="text" :value="announcementTitle" disabled />
                </div>
            </div>

            <div class="field">
                <label class="label">Message</label>
                <div class="control">
                    <div class="announcement-message-body">
                        <display-content :content="announcementBody || ''" />
                    </div>
                </div>
            </div>

            <div class="field">
                <label class="label">File Upload</label>
                <div class="control">
                    <input class="input" type="text" value="Files cannot be modified" disabled />
                </div>
            </div>

            <div class="field">
                <label class="label">Notify Enrolled Students</label>
                <div class="control">
                    <input
                        class="input"
                        type="text"
                        :value="announcementSendNotification ? 'Yes' : 'No'"
                        disabled
                    />
                </div>
            </div>

            <div class="field">
                <label class="label">Schedule</label>
                <div class="control">
                    <input
                        class="input"
                        type="text"
                        :value="formatDate(announcementPublishedAt)"
                        disabled
                    />
                </div>
            </div>

            <div class="field">
                <div class="control">
                    <label class="checkbox">
                        <e-switch v-model="localImportant" type="is-success">
                            Mark as Important
                        </e-switch>
                        <e-tooltip
                            label="This announcement will be marked as important and will stand out from the rest with a special icon."
                            position="is-right"
                        >
                            <e-icon
                                class="has-text-primary m-l-sm"
                                size="is-small"
                                icon="exclamation-circle"
                            />
                        </e-tooltip>
                    </label>
                </div>
            </div>
        </section>
        <footer class="modal-card-foot">
            <e-button :disabled="saving" @click="close">Cancel</e-button>
            <e-button type="is-primary" :loading="saving" :disabled="saving" @click="save">Save</e-button>
        </footer>
    </div>
</template>

<script setup>
import { inject, ref, watch } from 'vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import { initialState } from '@/Helpers.js';
import moment from '@/plugins/vue-moment.js';

const props = defineProps({
    announcementId: {
        type: [Number, String],
        required: true,
    },
    announcementTitle: {
        type: String,
        default: '',
    },
    announcementBody: {
        type: String,
        default: '',
    },
    announcementPublishedAt: {
        type: [String, Date],
        default: null,
    },
    announcementSendNotification: {
        type: Boolean,
        default: false,
    },
    important: {
        type: Boolean,
        default: false,
    },
    type: {
        type: String,
        required: true,
    },
    saving: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['save', 'close']);

// Inject timezone with fallback matching app-utilities.js logic
const injectedTimezone = inject('timezone', null);
const getTimezoneFallback = () => {
    const currentTimezone =
        window.config?.institution?.timezone || initialState('InstitutionSettings.timezone', 'America/Los_Angeles');
    return moment.tz.zone(currentTimezone);
};
const timezone = injectedTimezone ?? getTimezoneFallback();

const localImportant = ref(props.important);

watch(
    () => props.important,
    newVal => {
        localImportant.value = newVal;
    }
);

function formatDate(date) {
    if (!date) return 'Immediately';
    try {
        return moment.utc(date).tz(timezone?.name).format('MM/DD/YYYY hh:mm A');
    } catch (e) {
        return 'Immediately';
    }
}

function close(event) {
    if (event) {
        event.stopPropagation();
    }
    emit('close');
}

function save(event) {
    if (event) {
        event.stopPropagation();
        event.preventDefault();
    }

    if (props.saving) {
        return; // Prevent double-clicks
    }

    emit('save', localImportant.value);
}
</script>

<style scoped>
.announcement-message-body {
    max-height: clamp(200px, 35vh, 320px);
    overflow-y: auto;
    border: 1px solid var(--background, #f5f5f5);
    border-radius: 4px;
    background: var(--background, #f5f5f5);
    box-shadow: none;
    color: var(--text-light, #7a7a7a);
    cursor: not-allowed;
    display: block;
    font-size: 1rem;
    line-height: 1.5;
    min-height: 2.5em;
    padding: calc(0.5em - 1px) calc(0.75em - 1px);
    width: 100%;
}

.announcement-message-body :deep(.content) {
    margin: 0;
}

.announcement-message-body :deep(.content > :first-child) {
    margin-top: 0;
}

.announcement-message-body :deep(.content > :last-child) {
    margin-bottom: 0;
}
</style>
